import { useState, useEffect } from 'react';
import { useApp } from '@playcanvas/react/hooks';
import { Asset } from 'playcanvas';

interface LodMeta {
  lodLevels: number;
  filenames: string[];
  tree: {
    bound: {
      min: [number, number, number];
      max: [number, number, number];
    };
    children?: unknown[];
  };
}

interface UseLodSplatResult {
  asset: Asset | null;
  loading: boolean;
  error: Error | null;
  lodDistances: number[];
}

function calculateLodDistances(meta: LodMeta): number[] {
  const {min, max} = meta.tree.bound;
  const diagonal = Math.sqrt(
    (max[0] - min[0]) ** 2 +
    (max[1] - min[1]) ** 2 +
    (max[2] - min[2]) ** 2,
  );

  // Generate one distance threshold per LOD level
  // Each value is the max distance at which that LOD level is used
  // LOD 2 (lowest detail) should cover very far distances
  const numLods = meta.lodLevels;
  const distances: number[] = [];

  for (let i = 0; i < numLods; i++) {
    if (i === numLods - 1) {
      // Last LOD level: use a very large distance to cover everything
      distances.push(diagonal * 10);
    } else {
      // Earlier LOD levels: distribute distances
      distances.push(diagonal * (0.3 + i * 0.3));
    }
  }

  return distances;
}

export function useLodSplat(metaPath: string): UseLodSplatResult {
  const app = useApp();
  const [asset, setAsset] = useState<Asset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lodDistances, setLodDistances] = useState<number[]>([]);

  useEffect(() => {
    if (!app) return;

    let cancelled = false;
    let createdAsset: Asset | null = null;

    async function loadLodSplat() {
      try {
        // Fetch the lod-meta.json to get bounding box info
        const response = await fetch(metaPath);
        if (!response.ok) {
          throw new Error(`Failed to fetch ${metaPath}: ${response.statusText}`);
        }

        const meta: LodMeta = await response.json();

        if (!meta.tree?.bound) {
          throw new Error('Invalid lod-meta.json: missing tree.bound');
        }

        if (cancelled) return;

        // Calculate LOD distances from bounding box
        const distances = calculateLodDistances(meta);
        setLodDistances(distances);

        // Create the gsplat asset for LOD streaming
        createdAsset = new Asset('lod-splat', 'gsplat', {
          url: metaPath,
        });

        // Add to registry and load
        app.assets.add(createdAsset);

        await new Promise<void>((resolve, reject) => {
          createdAsset!.on('load', () => resolve());
          createdAsset!.on('error', (err: string) => reject(new Error(err)));
          app.assets.load(createdAsset!);
        });

        if (cancelled) {
          if (createdAsset) {
            app.assets.remove(createdAsset);
            createdAsset.unload();
          }
          return;
        }

        // Configure scene-wide LOD streaming settings (required for LOD to work)
        app.scene.gsplat.radialSorting = true;
        app.scene.gsplat.splatBudget = 700000;
        // Less aggressive culling settings to prevent tiles from disappearing
        // app.scene.gsplat.lodUpdateAngle = 180;      // Only update LOD on large rotations
        app.scene.gsplat.lodUpdateAngle = 5;
        app.scene.gsplat.lodBehindPenalty = 2;      // Don't penalize tiles behind camera
        app.scene.gsplat.lodUpdateDistance = 1;
        // app.scene.gsplat.lodUpdateDistance = 100;    // Less frequent position-based updates
        app.scene.gsplat.lodUnderfillLimit = 200;   // Allow more underfill
        app.scene.gsplat.lodRangeMin = 0;
        // Match the actual number of LOD levels in the data
        app.scene.gsplat.lodRangeMax = meta.lodLevels - 1;

        // Debug: colorize tiles by LOD level to visualize what's happening
        app.scene.gsplat.colorizeLod = false;

        // Log splat stats every frame for debugging
        // console.log('LOD streaming configured:', {
        //   lodLevels: meta.lodLevels,
        //   lodRangeMin: app.scene.gsplat.lodRangeMin,
        //   lodRangeMax: app.scene.gsplat.lodRangeMax,
        //   distances,
        // });

        setAsset(createdAsset);
        setLoading(false);

      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load LOD splat:', err);
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      }
    }

    loadLodSplat();

    return () => {
      cancelled = true;
      if (createdAsset) {
        app.assets.remove(createdAsset);
        createdAsset.unload();
      }
    };
  }, [app, metaPath]);

  return {asset, loading, error, lodDistances};
}
